/**
 * useDeadlineAlerts Hook
 *
 * Computes count of OVERDUE + CRITICAL deadlines for sidebar badge.
 * Caches in sessionStorage with 5-minute TTL to avoid redundant API calls.
 */

import { useEffect, useState } from 'react';

import { getDeadlinesFromServices } from '../services/calendarService';
import { getMyServices } from '../services/servicesService';

const CACHE_KEY = 'geauxfile:deadline-alerts';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedAlerts {
  count: number;
  timestamp: number;
}

export function useDeadlineAlerts(): number {
  const [count, setCount] = useState(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed: CachedAlerts = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_TTL_MS) {
          return parsed.count;
        }
      }
    } catch {
      // Ignore parse errors
    }
    return 0;
  });

  useEffect(() => {
    // Check if cache is still valid
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed: CachedAlerts = JSON.parse(cached);
        if (Date.now() - parsed.timestamp < CACHE_TTL_MS) {
          setCount(parsed.count);
          return;
        }
      }
    } catch {
      // Continue to fetch
    }

    let cancelled = false;

    async function fetch() {
      try {
        const result = await getMyServices();
        const deadlines = getDeadlinesFromServices(result.services);
        const alertCount = deadlines.filter(
          (d) => d.urgency === 'OVERDUE' || d.urgency === 'CRITICAL',
        ).length;

        if (!cancelled) {
          setCount(alertCount);
          sessionStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ count: alertCount, timestamp: Date.now() }),
          );
        }
      } catch {
        // Don't show badge on error
      }
    }

    void fetch();
    return () => { cancelled = true; };
  }, []);

  return count;
}
